import { apiFetch } from "@/lib/api";

export const MISC_QBT_TIME_CARDS_QUERY_KEY = ["personal", "quickbooks-time", "time-cards"] as const;

export const ADMIN_TIME_CARD_CHANGE_REQUESTS_QUERY_KEY = [
  "admin",
  "time-card-change-requests",
  "pending",
] as const;

export type MiscTimeCardRowSource = "qbt" | "misc";

export type TimeCardChangeAction =
  | "edit_qbt_timesheet"
  | "add_qbt_timesheet"
  | "delete_qbt_timesheet"
  | "edit_misc_entry"
  | "delete_misc_entry";

export interface MiscTimeCardRow {
  rowKey: string;
  source: MiscTimeCardRowSource;
  calendarDate: string;
  label: string;
  start: string | null;
  end: string | null;
  durationSeconds: number | null;
  jobcodeId: number | null;
  quickbooksTimeTimesheetId: number | null;
  laborMiscTimeEntryId: number | null;
  pendingChangeRequestId: number | null;
}

export interface MiscTimeCardPeriodSection {
  period: {
    key: "current" | "previous";
    label: string;
    startDate: string;
    endDate: string;
  };
  rows: MiscTimeCardRow[];
}

export interface MiscTimeCardPayPeriodsResponse {
  oauthConnected: boolean;
  quickbooksTimeUserId: number | null;
  quickbooksTimeUserMatched: boolean;
  current: MiscTimeCardPeriodSection;
  previous: MiscTimeCardPeriodSection;
}

export interface TimeCardChangeRequest {
  id: number;
  status: "pending" | "approved" | "rejected" | "cancelled";
  action: TimeCardChangeAction;
  subjectUserId: string;
  subjectUserLabel: string | null;
  calendarDate: string;
  quickbooksTimeUserId: number | null;
  quickbooksTimeTimesheetId: number | null;
  laborMiscTimeEntryId: number | null;
  originalPayload: Record<string, unknown>;
  proposedPayload: Record<string, unknown>;
  reason: string;
  requestedByUserId: string;
  requestedByLabel: string | null;
  requestedAt: string;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolvedByLabel: string | null;
  resolutionNotes: string | null;
  applyError: string | null;
}

export interface CreateTimeCardChangeRequestBody {
  action: TimeCardChangeAction;
  calendarDate: string;
  quickbooksTimeTimesheetId?: number | null;
  laborMiscTimeEntryId?: number | null;
  proposedPayload: Record<string, unknown>;
  reason: string;
}

export async function fetchMiscTimeCardPayPeriods(): Promise<MiscTimeCardPayPeriodsResponse> {
  const response = await apiFetch<{ timeCards: MiscTimeCardPayPeriodsResponse }>(
    "/api/personal/quickbooks-time/time-cards",
  );
  return response.timeCards;
}

export async function postMiscTimeCardChangeRequest(
  body: CreateTimeCardChangeRequestBody,
): Promise<TimeCardChangeRequest> {
  const response = await apiFetch<{ changeRequest: TimeCardChangeRequest }>(
    "/api/personal/quickbooks-time/change-requests",
    {
      method: "POST",
      body,
    },
  );
  return response.changeRequest;
}

export async function postMiscCancelTimeCardChangeRequest(requestId: number): Promise<TimeCardChangeRequest> {
  const response = await apiFetch<{ changeRequest: TimeCardChangeRequest }>(
    `/api/personal/quickbooks-time/change-requests/${requestId}/cancel`,
    { method: "POST" },
  );
  return response.changeRequest;
}

export async function fetchAdminPendingTimeCardChangeRequests(): Promise<TimeCardChangeRequest[]> {
  const response = await apiFetch<{ requests: TimeCardChangeRequest[] }>(
    "/api/user-admin/time-card-change-requests/pending",
  );
  return response.requests;
}

export async function postAdminApproveTimeCardChangeRequest(input: {
  requestId: number;
  resolutionNotes?: string | null;
}): Promise<TimeCardChangeRequest> {
  const response = await apiFetch<{ changeRequest: TimeCardChangeRequest }>(
    `/api/user-admin/time-card-change-requests/${input.requestId}/approve`,
    {
      method: "POST",
      body: { resolutionNotes: input.resolutionNotes ?? null },
    },
  );
  return response.changeRequest;
}

export async function postAdminRejectTimeCardChangeRequest(input: {
  requestId: number;
  resolutionNotes?: string | null;
}): Promise<TimeCardChangeRequest> {
  const response = await apiFetch<{ changeRequest: TimeCardChangeRequest }>(
    `/api/user-admin/time-card-change-requests/${input.requestId}/reject`,
    {
      method: "POST",
      body: { resolutionNotes: input.resolutionNotes ?? null },
    },
  );
  return response.changeRequest;
}

export function formatMiscTimeCardDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) {
    return "—";
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export function formatMiscTimeCardActionLabel(action: TimeCardChangeAction): string {
  switch (action) {
    case "edit_qbt_timesheet":
      return "Edit QuickBooks Time row";
    case "add_qbt_timesheet":
      return "Add QuickBooks Time row";
    case "delete_qbt_timesheet":
      return "Delete QuickBooks Time row";
    case "edit_misc_entry":
      return "Edit hub break row";
    case "delete_misc_entry":
      return "Delete hub break row";
    default:
      return action;
  }
}

export function formatMiscTimeCardInstant(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "Open";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
}

/** Convert a datetime-local value to ISO in America/Chicago for API payloads. */
export function miscTimeCardDateTimeLocalToIso(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return trimmed;
  }
  return parsed.toISOString();
}

/** Format API ISO timestamp for datetime-local input (browser local). */
export function miscTimeCardIsoToDateTimeLocal(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}
